import renderSelect from 'components/FormControls/select'
import GridSpinner from 'components/UI/GridSpinner'
import { Badge, Button, Grid, Icon, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Field } from 'redux-form'
import { createPath } from 'history'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import qs from 'qs'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const PaymentProfilesControl = ({
  className = '',
  loading = false,
  disabled = false,
  paymentProfiles = null,
  t = () => {},
  readOnly = false,
  addUrlParams = {},
}) => {
  const breakpoint = useBreakpoint()
  const pathGen = useGeneratePathI18n()

  const paymentProfilesOptions = useMemo(
    () =>
      !paymentProfiles || paymentProfiles.length === 0
        ? []
        : paymentProfiles.map((profile) => ({
            ...profile,
            disabled: profile.status === 'declined',
            value: profile.id,
          })),
    [paymentProfiles],
  )

  if (!paymentProfiles) {
    return null
  }

  return (
    <div className={className}>
      <Typography
        component={'h3'}
        variant={'Subtitle-30'}
        className={'margin-bottom-24'}
      >
        {t(`services::forms.shared_fields.payment_profile_id.header`)}
      </Typography>
      <Field
        disabled={disabled}
        readOnly={readOnly}
        component={renderSelect}
        name={'payment_profile_id'}
        fullWidth
        t={t}
        showEmptyOption={!readOnly}
        emptyOptionLabel={t(`services::forms.shared_fields.payment_profile_id.options.empty`)}
        clearOptionLabel={t('common::select_clear_option')}
        helperText={
          !loading && paymentProfiles.length === 0 ? (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: t(`services::forms.shared_fields.payment_profile_id.helperText.empty`),
                }}
              />{' '}
              <Button
                variant={'primary'}
                link
                size={'s'}
                component={Link}
                disabled={disabled}
                to={createPath({
                  pathname: pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW),
                  search: qs.stringify(
                    {
                      ...addUrlParams,
                      goBack: global.location.pathname + global.location.search,
                    },
                    {
                      arrayFormat: 'brackets',
                    },
                  ),
                })}
              >
                <Typography variant={'Body-10'}>
                  {t(
                    `services::forms.shared_fields.payment_profile_id.helperText.empty_create_label`,
                  )}
                </Typography>
              </Button>
            </>
          ) : null
        }
        label={t(`services::forms.shared_fields.payment_profile_id.label`)}
        activeOptionLabelTemplate={(item) => {
          const tax_number = item?.tax_info?.tax_number ? ' — ' + item.tax_info.tax_number : ''
          return `${item.title}${tax_number}`
        }}
        optionLabelTemplate={(payment_profile) => {
          const {
            status,
            tax_info = {},
            contract = undefined,
            decline_reason = null,
            title = '',
            type = undefined,
          } = payment_profile
          return (
            <Grid
              direction={'column'}
              gap={4}
            >
              <Typography variant={'Body-15'}>{title}</Typography>
              <Grid
                gap={8}
                flexAlign={'center'}
              >
                {type && (
                  <Badge
                    rounded
                    bgColor={'Background-Six'}
                    color={'Label-GU-Primary'}
                  >
                    {t(`payment_profiles::types.${type}`, {
                      context: 'short',
                    })}
                  </Badge>
                )}

                <Badge
                  rounded
                  bgColor={'Background-Six'}
                >
                  ИНН {tax_info?.tax_number}
                </Badge>

                {contract?.number && (
                  <Typography
                    variant={'Body-10'}
                    color={'Label-Secondary'}
                  >
                    {contract.number}
                  </Typography>
                )}

                {status === 'declined' && (
                  <Typography
                    variant={'Body-10'}
                    color={'Label-Error-Primary'}
                  >
                    {t(
                      'services::forms.shared_fields.payment_profile_id.options.error_description',
                    )}{' '}
                    {decline_reason ? `: ${decline_reason}` : ''}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )
        }}
        options={paymentProfilesOptions}
        ControlProps={{
          startAdornment: loading && <GridSpinner width={24} />,
          endAdornment: (
            <Button
              noLabel={!breakpoint.sm}
              size={'l'}
              color={'Label-GU-Primary'}
              component={Link}
              variant={'link'}
              disabled={disabled}
              to={createPath({
                pathname: pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW),
                search: qs.stringify(
                  {
                    ...addUrlParams,
                    goBack: global.location.pathname + global.location.search,
                  },
                  {
                    arrayFormat: 'brackets',
                  },
                ),
              })}
              startIcon={<Icon name={'plus'} />}
            >
              {t(`common::buttons.${!loading && paymentProfiles.length === 0 ? 'create' : 'add'}`)}
            </Button>
          ),
        }}
      />
    </div>
  )
}
export default PaymentProfilesControl

PaymentProfilesControl.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  paymentProfiles: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  t: PropTypes.func,
  addUrlParams: PropTypes.object,
}
